import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Grip, X } from 'lucide-react';

type PageGalleryProps = {
    images: string[];
    name: string;
};

// Fotos exibidas no mosaico do desktop; as demais ficam em "Ver todas".
const MOSAIC_LIMIT = 5;

// Trava a rolagem da pagina enquanto um overlay estiver aberto.
const useBodyScrollLock = (locked: boolean) => {
    useEffect(() => {
        if (!locked) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [locked]);
};

// Posicao de cada foto no mosaico conforme a quantidade (grade de 4 colunas x 2 linhas).
const mosaicClass = (count: number, position: number) => {
    if (count === 1) return 'col-span-4 row-span-2';
    if (count === 2) return 'col-span-2 row-span-2';
    if (count === 3) return position === 0 ? 'col-span-2 row-span-2' : 'col-span-2';
    if (count === 4) return position === 0 ? 'col-span-2 row-span-2' : position === 3 ? 'col-span-2' : '';
    return position === 0 ? 'col-span-2 row-span-2' : '';
};

const Photo = ({ src, alt, className = '', onClick, children }: { src: string; alt: string; className?: string; onClick: () => void; children?: ReactNode }) => (
    <button
        type="button"
        onClick={onClick}
        className={`group relative block h-full w-full overflow-hidden bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ${className}`}
    >
        <img src={src} alt={alt} loading="lazy" draggable={false} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
        <span className="absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/10" aria-hidden></span>
        {children}
    </button>
);

export function PageGallery({ images, name }: PageGalleryProps) {
    const [index, setIndex] = useState<number | null>(null);
    const [showAll, setShowAll] = useState(false);
    const [mobileActive, setMobileActive] = useState(0);
    const total = images.length;
    const mosaic = images.slice(0, MOSAIC_LIMIT);
    const hidden = total - mosaic.length;

    useBodyScrollLock(index !== null || showAll);

    useEffect(() => {
        if (index === null && !showAll) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (index !== null) {
                if (event.key === 'Escape') setIndex(null);
                if (event.key === 'ArrowRight') setIndex((value) => (value === null ? value : (value + 1) % total));
                if (event.key === 'ArrowLeft') setIndex((value) => (value === null ? value : (value - 1 + total) % total));
                return;
            }
            if (event.key === 'Escape') setShowAll(false);
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [index, showAll, total]);

    const viewAllButton = total > 1 && (
        <button
            type="button"
            onClick={() => setShowAll(true)}
            className="inline-flex items-center gap-2 rounded-full bg-base-100/95 px-4 py-2 text-sm font-medium text-base-content shadow-sm ring-1 ring-base-content/[0.08] backdrop-blur-md transition hover:bg-base-100"
        >
            <Grip className="text-xs text-base-content/60" aria-hidden="true" />
            Ver todas as {total} fotos
        </button>
    );

    return (
        <>
            {/* Mobile: carrossel compacto com contador. */}
            <div className="relative sm:hidden">
                <div
                    className="-mx-5 flex snap-x snap-mandatory scroll-px-5 gap-2.5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    onScroll={(event) => {
                        const el = event.currentTarget;
                        const card = el.firstElementChild as HTMLElement | null;
                        const step = card ? card.offsetWidth + 10 : el.clientWidth;
                        setMobileActive(Math.min(total - 1, Math.round(el.scrollLeft / step)));
                    }}
                >
                    {images.map((src, position) => (
                        <div key={`${src}-${position}`} className={`shrink-0 snap-start ${total === 1 ? 'w-full' : 'w-[82%]'}`}>
                            <Photo src={src} alt={`${name} — foto ${position + 1}`} className="aspect-[4/5] rounded-2xl" onClick={() => setIndex(position)} />
                        </div>
                    ))}
                </div>
                {total > 1 && (
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs tabular-nums text-base-content/50">
                            {mobileActive + 1} de {total}
                        </span>
                        <button type="button" onClick={() => setShowAll(true)} className="text-sm font-medium text-primary">
                            Ver todas
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop: mosaico de altura fixa; excedente vira "+N" e "Ver todas". */}
            <div className="relative hidden sm:block">
                <div className="grid h-[26rem] grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-3xl lg:h-[30rem]">
                    {mosaic.map((src, position) => {
                        const isLast = position === mosaic.length - 1 && hidden > 0;
                        return (
                            <div key={`${src}-${position}`} className={`min-h-0 ${mosaicClass(mosaic.length, position)}`}>
                                <Photo
                                    src={src}
                                    alt={`${name} — foto ${position + 1}`}
                                    onClick={() => (isLast ? setShowAll(true) : setIndex(position))}
                                >
                                    {isLast && (
                                        <span className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 text-white backdrop-blur-[1px]">
                                            <span className="text-2xl font-semibold">+{hidden}</span>
                                            <span className="text-xs text-white/80">fotos</span>
                                        </span>
                                    )}
                                </Photo>
                            </div>
                        );
                    })}
                </div>
                {total > 1 && <div className="absolute bottom-4 right-4">{viewAllButton}</div>}
            </div>

            {/* Todas as fotos em grade, sem alongar a pagina. */}
            {showAll &&
                createPortal(
                    <div className="fixed inset-0 z-[950] flex flex-col bg-base-200" role="dialog" aria-modal="true" aria-label={`Fotos de ${name}`}>
                        <div className="flex h-14 shrink-0 items-center justify-between border-b border-base-content/[0.06] bg-base-200/80 px-4 backdrop-blur-xl sm:px-6">
                            <button
                                type="button"
                                onClick={() => setShowAll(false)}
                                className="flex items-center gap-1.5 text-[0.9375rem] font-medium text-primary"
                            >
                                <ChevronLeft className="text-sm" aria-hidden="true" />
                                Voltar
                            </button>
                            <span className="text-sm font-semibold">{total} fotos</span>
                            <span className="w-16" aria-hidden></span>
                        </div>
                        <div className="flex-1 overflow-y-auto overscroll-contain">
                            <ul className="mx-auto grid max-w-6xl grid-cols-2 gap-1.5 p-1.5 sm:grid-cols-3 sm:gap-2 sm:p-6 lg:grid-cols-4">
                                {images.map((src, position) => (
                                    <li key={`${src}-${position}`} className="aspect-square">
                                        <Photo src={src} alt={`${name} — foto ${position + 1}`} className="rounded-lg sm:rounded-xl" onClick={() => setIndex(position)} />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>,
                    document.body,
                )}

            {index !== null &&
                createPortal(
                    <div
                        className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/95"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Foto ${index + 1} de ${total}`}
                        onClick={() => setIndex(null)}
                    >
                        <img
                            src={images[index]}
                            alt={`${name} — foto ${index + 1}`}
                            className="max-h-[86vh] max-w-[94vw] rounded-xl object-contain"
                            onClick={(event) => event.stopPropagation()}
                        />
                        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4 text-white">
                            <span className="text-sm tabular-nums text-white/70">
                                {index + 1} de {total}
                            </span>
                            <button
                                type="button"
                                onClick={() => setIndex(null)}
                                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/25"
                                aria-label="Fechar"
                            >
                                <X aria-hidden="true" />
                            </button>
                        </div>
                        {total > 1 && (
                            <>
                                <button
                                    type="button"
                                    className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setIndex((index - 1 + total) % total);
                                    }}
                                    aria-label="Foto anterior"
                                >
                                    <ChevronLeft aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setIndex((index + 1) % total);
                                    }}
                                    aria-label="Próxima foto"
                                >
                                    <ChevronRight aria-hidden="true" />
                                </button>
                            </>
                        )}
                    </div>,
                    document.body,
                )}
        </>
    );
}
